import { CONFIG, type Config } from "@packages/config";
import { window } from "@packages/global";
import { Inject, Injectable } from "@webmotors/ioc";
import { HTTP, type Http, type HttpConfig, HttpMethod } from "../http";
import {
  SESSION_STORAGE_SERVICE,
  SessionStorageService,
} from "../session-storage";
import { API } from "./api.types";

@Injectable(API)
export class ApiService {
  /**
   * API Service constructor
   *
   * @param http
   * @param config
   */
  constructor(
    @Inject(HTTP)
    private readonly http: Http,
    @Inject(CONFIG)
    private readonly config: Config,
    @Inject(SESSION_STORAGE_SERVICE)
    private readonly sessionStorageService: SessionStorageService
  ) {}

  /**
   * Converts response to json
   *
   * @param payload
   * @returns
   */
  private json<Response>(payload: unknown): Response {
    try {
      if (typeof payload !== "string") {
        return payload as Response;
      }

      return JSON.parse(payload) as Response;
    } catch {
      return {} as Response;
    }
  }

  /**
   * Sends a HTTP request to the Webmotors rest API
   *
   * @param path
   * @param config
   * @returns
   */
  async request(path: string, config: HttpConfig): Promise<string> {
    try {
      const requestPath = path.startsWith("/") ? path : `/${path}`;

      const requestBaseURL = this.config.isServerSide()
        ? this.config.apiPrivateURL
        : this.config.apiURL;

      const requestURL = `${requestBaseURL}${requestPath}`;

      const availableDomains = [
        "localhost:3000",
        "localhost:3001",
        "hportal.webmotors.com.br",
        "hportal.webmotors.com.br/assinatura",
      ];

      const hasGuid = availableDomains.includes(this.config.domain);

      const headers = {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...(hasGuid && {
          "X-Custom-Guid": "dc75bb4f-625e-4876-a2d8-150c21c784e2",
        }),
        ...(config.headers || {}),
      };

      const response = await this.http.request(requestURL, {
        ...config,
        headers,
      });

      return response;
    } catch (error) {
      const wasRefreshed = this.sessionStorageService.getItem<boolean>(
        "perimeterx_refreshed"
      );

      if (
        (await this.http.isPerimeterexError(error)) &&
        !this.config.isServerSide() &&
        !wasRefreshed
      ) {
        this.sessionStorageService.setItem("perimeterx_refreshed", true);

        window.location.reload();

        return "{}";
      }

      throw error;
    }
  }

  /**
   * Sends a POST HTTP request to the Webmotors rest API
   *
   * @param path
   * @param config
   * @returns
   */
  async post<Request, Response>(
    path: string,
    data?: Request,
    config?: HttpConfig
  ): Promise<Response> {
    const response = await this.request(path, {
      body: JSON.stringify(data ?? {}),
      method: HttpMethod.POST,
      ...(config || {}),
    });

    return this.json<Response>(response);
  }

  /**
   * Sends a PUT HTTP request to the Webmotors rest API
   *
   * @param path
   * @param config
   * @returns
   */
  async put<Request, Response>(
    path: string,
    data?: Request,
    config?: HttpConfig
  ): Promise<Response> {
    const response = await this.request(path, {
      body: JSON.stringify(data ?? {}),
      method: HttpMethod.PUT,
      ...(config || {}),
    });

    return this.json<Response>(response);
  }

  /**
   * Sends a PATCH HTTP request to the Webmotors rest API
   *
   * @param path
   * @param config
   * @returns
   */
  async patch<Request, Response>(
    path: string,
    data?: Request,
    config?: HttpConfig
  ): Promise<Response> {
    const response = await this.request(path, {
      body: JSON.stringify(data ?? {}),
      method: HttpMethod.PATCH,
      ...(config || {}),
    });

    return this.json<Response>(response);
  }

  /**
   * Sends a DELETE HTTP request to the Webmotors rest API
   *
   * @param path
   * @param config
   * @returns
   */
  async delete<Request, Response>(
    path: string,
    data?: Request,
    config?: HttpConfig
  ): Promise<Response> {
    const response = await this.request(path, {
      body: JSON.stringify(data ?? {}),
      method: HttpMethod.DELETE,
      ...(config || {}),
    });

    return this.json<Response>(response);
  }

  /**
   * Sends a GET HTTP request to the Webmotors rest API
   *
   * @param path
   * @param config
   * @returns
   */
  async get<Request, Response>(
    path: string,
    data?: Request,
    config?: HttpConfig
  ): Promise<Response> {
    const response = await this.request(path, {
      params: data ?? {},
      method: HttpMethod.GET,
      ...(config || {}),
    });

    return this.json<Response>(response);
  }
}
